import type { KeyboardEvent, MouseEvent, ReactNode } from "react"
import { useLayoutEffect, useRef, useState } from "react"

import { cx } from "../lib/cx"
import { formatTimeOfDay } from "../lib/format"
import { initialsOf, masterAvatarStyle, masterTintStyle } from "../theme/master-color"
import { AppointmentCard } from "./appointment-card"
import {
  cardHeight,
  columnWidth,
  GRID_MASTER_HEADER,
  GRID_TIME_COLUMN,
  GRID_UNASSIGNED_COLUMN,
  gridHeight,
  gridLabels,
  gridLines,
  minuteToTop,
  pixelsPerMinute,
  totalWidth,
} from "./grid-geometry"
import styles from "./journal-grid.module.css"
import type { JournalBusy, JournalColumn, JournalDayModel, JournalHold } from "./journal-model"
import { holdExplanation } from "./journal-model"
import { formatCountdown, formatMinute } from "./journal-time"
import { useNowMinute, useSecondsNow } from "./use-journal-clock"
import { useElementWidth } from "./use-viewport"

/**
 * Сетка дня.
 *
 * ПОЧЕМУ ВСЁ ПОЗИЦИОНИРОВАНО АБСОЛЮТНО, А НЕ ТАБЛИЦЕЙ ИЛИ CSS GRID. Запись может начаться
 * между линиями: в режиме упаковки `tight` следующий слот начинается сразу после конца
 * предыдущей записи, а не по решётке (ТЗ §2.2.1). Табличная вёрстка такую запись обязана
 * округлить до ближайшей ячейки — то есть соврать о времени. Абсолютная координата
 * от минуты этого не делает: 14:05 при шаге 5 стоит на 14:05, а не «примерно там».
 *
 * ЧТО ЗАКРЕПЛЕНО. Шкала времени слева и шапка мастеров сверху. При пятнадцати мастерах
 * и шаге 15 прокрутка идёт в обе стороны, и без этих двух закреплений администратор
 * перестаёт понимать, на чей час он смотрит.
 */

export function JournalGrid(props: {
  readonly model: JournalDayModel
  readonly step: number
  /** Проверка различимости статусов без цвета — критерий приёмки ТЗ §2.3.3. */
  readonly grayscale?: boolean
  /** Подсказка поверх готовой сетки: «записей нет, кликните на свободное время». */
  readonly hint?: string
  /**
   * Открыть панель записи. Пусто — день только для чтения: карточки не кликаются,
   * курсор над свободным местом «запрещено». Так выглядит офлайн и технические работы.
   */
  readonly onOpenCard?: ((cardId: string) => void) | undefined
  /** Клик по свободному месту: мастер и минута начала, округлённая вниз по шагу. */
  readonly onPickSlot?: ((employeeId: string | null, minute: number) => void) | undefined
  /** Запись, чья панель открыта: её карточка обведена. */
  readonly selectedCardId?: string | null
  /**
   * Изменения выключены (офлайн, read_only, нет прав). Отличается от «нет обработчика»
   * тем, что курсор говорит «сюда нельзя», а не «здесь ничего нет».
   */
  readonly actionsDisabled?: boolean
  /**
   * Срок удержания из правил салона: в объяснении истёкшего удержания («слот держался
   * 5 минут и был отпущен»). null — правила не загружены, и число не выдумывается.
   */
  readonly holdTtlSeconds?: number | null
  /** Снять СВОЁ удержание. Без обработчика объяснение остаётся, а кнопки нет. */
  readonly onReleaseHold?: ((holdId: string) => void) | undefined
  /** Перечитать день: истёкшее удержание исчезает из сетки только перезагрузкой. */
  readonly onRefresh?: (() => void) | undefined
}): ReactNode {
  const { model, step } = props

  /*
   * Объяснение показывается по нажатию, а не наведением: у нажатия один и тот же ответ
   * на мыши и на планшете, а подсказка по наведению на планшете не существует вовсе.
   * Открыто всегда ОДНО объяснение — иначе сетка обрастает пузырями, которые нечем
   * закрыть, кроме перезагрузки.
   */
  const [explainedHoldId, setExplainedHoldId] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const available = useElementWidth(scrollRef)

  const open = model.window.openMinute
  const close = model.window.closeMinute
  const height = gridHeight(open, close, step)
  const perMinute = pixelsPerMinute(step)

  // Ширина колонки считается от РЕАЛЬНОГО места, а не от 1366: у владельца сети слева
  // навигация 56 px вместо 200, и колонка там шире на те же 144.
  const forColumns = Math.max(available - GRID_TIME_COLUMN - GRID_UNASSIGNED_COLUMN, 0)
  const width = columnWidth(forColumns, model.columns.length)
  const layerWidth = totalWidth(width, model.columns.length)
  const unassignedLeft = GRID_TIME_COLUMN + width * model.columns.length

  const lines = gridLines(open, close, step)
  const labels = gridLabels(open, close, step)

  const nowMinute = useNowMinute(model.timeZone, model.date)
  const nowVisible = nowMinute >= open && nowMinute <= close
  const nowTop = minuteToTop(nowMinute, open, step)

  // Обратный отсчёт удержаний идёт раз в секунду и только когда удержания есть.
  const hasHolds = model.columns.some((column) => column.holds.length > 0)
  const secondsNow = useSecondsNow(hasHolds)

  // При открытии дня «сейчас» ставится в верхнюю треть (ТЗ §2.2.1): администратору нужен
  // ближайший час и следующий за ним, а не утро, которое уже прошло.
  //
  // Прокрутка делается РОВНО ОДИН РАЗ на день — это сторожит ref, а не список
  // зависимостей. Линия «сейчас» переползает каждую минуту, и без сторожа сетка каждую
  // минуту прыгала бы к ней, вырывая экран из-под руки у того, кто смотрит на вечер.
  const scrolledFor = useRef<string | null>(null)
  useLayoutEffect(() => {
    const element = scrollRef.current
    if (element === null || available === 0) return
    const key = `${model.date}|${model.timeZone}`
    if (scrolledFor.current === key) return
    scrolledFor.current = key
    if (!nowVisible) return
    element.scrollTop = Math.max(0, nowTop - element.clientHeight / 3)
  }, [model.date, model.timeZone, available, nowTop, nowVisible])

  return (
    <div className={styles["frame"]}>
      {/* Полностью настроенный салон без записей — это НЕ пустое состояние, а работающая
          сетка со сменами и свободным временем. Поверх неё кладётся ненавязчивая
          подсказка, а не заглушка вместо дня (ТЗ §2.11.2). */}
      {props.hint === undefined ? null : <div className={styles["hint"]}>{props.hint}</div>}
      <div
        ref={scrollRef}
        className={cx(
          styles["scroll"],
          props.grayscale === true ? styles["checkGrayscale"] : undefined,
        )}
      >
        <div
        className={styles["layer"]}
        style={{ width: `${layerWidth}px`, height: `${height + GRID_MASTER_HEADER}px` }}
      >
        <div className={styles["header"]} style={{ width: `${layerWidth}px` }}>
          <div className={styles["headerCorner"]} />
          {model.columns.map((column) => (
            <MasterHeaderCell key={column.employee.id} column={column} width={width} />
          ))}
          <div className={styles["headerUnassigned"]}>Без мастера</div>
        </div>

        <div
          className={styles["body"]}
          style={{ width: `${layerWidth}px`, height: `${height}px` }}
        >
          {lines.map((line) => (
            <div
              key={line.minute}
              className={cx(
                styles["line"],
                line.weight === "hour"
                  ? styles["lineHour"]
                  : line.weight === "half"
                    ? styles["lineHalf"]
                    : styles["lineMinor"],
              )}
              style={{ top: `${line.top}px` }}
              aria-hidden="true"
            />
          ))}

          <div className={styles["scale"]} style={{ height: `${height}px` }}>
            {labels.map((label) => (
              <div
                key={label.minute}
                className={cx(
                  styles["label"],
                  label.isHour ? styles["labelHour"] : styles["labelHalf"],
                  label.isFirst ? styles["labelFirst"] : undefined,
                )}
                style={{ top: `${label.top}px` }}
              >
                {label.text}
              </div>
            ))}
            {nowVisible ? (
              <span className={styles["nowBadge"]} style={{ top: `${nowTop}px` }}>
                {formatMinute(nowMinute)}
              </span>
            ) : null}
          </div>

          {model.columns.map((column, index) => (
            <ColumnLayer
              key={column.employee.id}
              column={column}
              left={GRID_TIME_COLUMN + index * width}
              width={width}
              height={height}
              open={open}
              step={step}
              perMinute={perMinute}
              secondsNow={secondsNow}
              timeZone={model.timeZone}
              onOpenCard={props.onOpenCard}
              onPickSlot={props.onPickSlot}
              selectedCardId={props.selectedCardId ?? null}
              actionsDisabled={props.actionsDisabled === true}
              holdTtlSeconds={props.holdTtlSeconds ?? null}
              explainedHoldId={explainedHoldId}
              onExplain={setExplainedHoldId}
              onReleaseHold={props.onReleaseHold}
              onRefresh={props.onRefresh}
            />
          ))}

          <div
            className={styles["unassigned"]}
            style={{ left: `${unassignedLeft}px`, height: `${height}px` }}
          >
            {model.unassigned.length === 0 ? (
              <div className={styles["unassignedEmpty"]}>Все записи распределены</div>
            ) : (
              model.unassigned.map((card) => {
                // У неразобранной записи высота не «перерастает», а берётся не меньше 34:
                // без строки «назначьте мастера» карточка не объясняет, что с ней делать.
                const cardTop = minuteToTop(card.startMinute, open, step)
                const cardH = Math.max(Math.round(card.durationMinutes * perMinute), 34)
                const openCard = props.onOpenCard
                return (
                  <div
                    key={card.id}
                    className={cx(
                      styles["unassignedCard"],
                      openCard === undefined ? undefined : styles["unassignedCardOpen"],
                    )}
                    style={{ top: `${cardTop}px`, height: `${cardH}px` }}
                    title={card.tooltip}
                    role={openCard === undefined ? undefined : "button"}
                    tabIndex={openCard === undefined ? undefined : 0}
                    onClick={() => {
                      openCard?.(card.id)
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return
                      event.preventDefault()
                      openCard?.(card.id)
                    }}
                  >
                    <div className={styles["unassignedName"]}>{card.clientName}</div>
                    <div className={styles["unassignedTime"]}>
                      {card.timeLabel} · назначьте мастера
                    </div>
                    {card.durationMinutes * perMinute >= 48 ? (
                      <div className={styles["cardService"]}>{card.serviceName}</div>
                    ) : null}
                  </div>
                )
              })
            )}
          </div>

          {nowVisible ? (
            <div className={styles["nowLine"]} style={{ top: `${nowTop}px` }} aria-hidden="true" />
          ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function MasterHeaderCell(props: {
  readonly column: JournalColumn
  readonly width: number
}): ReactNode {
  const { employee } = props.column
  return (
    <div
      className={cx(styles["headerCell"], "plnr-master-tint")}
      style={{ ...masterTintStyle(employee.color), width: `${props.width}px` }}
    >
      {/* Аватар показывает цвет мастера КАК ЕСТЬ — второе и последнее такое место. */}
      <span className={styles["avatar"]} style={masterAvatarStyle(employee.color)}>
        {initialsOf(employee.displayName)}
      </span>
      <span className={styles["headerText"]}>
        <span className={styles["headerName"]} title={employee.displayName}>
          {employee.displayName}
        </span>
        {employee.positionName === null ? null : (
          <span className={styles["headerPosition"]}>{employee.positionName}</span>
        )}
      </span>
    </div>
  )
}

function ColumnLayer(props: {
  readonly column: JournalColumn
  readonly left: number
  readonly width: number
  readonly height: number
  readonly open: number
  readonly step: number
  readonly perMinute: number
  readonly secondsNow: number
  readonly timeZone: string
  readonly onOpenCard?: ((cardId: string) => void) | undefined
  readonly onPickSlot?: ((employeeId: string | null, minute: number) => void) | undefined
  readonly selectedCardId: string | null
  readonly actionsDisabled: boolean
  readonly holdTtlSeconds: number | null
  readonly explainedHoldId: string | null
  readonly onExplain: (holdId: string | null) => void
  readonly onReleaseHold?: ((holdId: string) => void) | undefined
  readonly onRefresh?: (() => void) | undefined
}): ReactNode {
  const { column, open, step, perMinute, height } = props
  const pickable = props.onPickSlot !== undefined && !props.actionsDisabled
  const workStart = column.workStartMinute
  const workEnd = column.workEndMinute
  const hasShift = workStart !== null && workEnd !== null

  const workTop = hasShift ? Math.max(minuteToTop(workStart, open, step), 0) : 0
  const workBottom = hasShift ? Math.min(minuteToTop(workEnd, open, step), height) : 0

  /*
   * Минута из координаты нажатия, округлённая ВНИЗ по шагу сетки. Округление вниз,
   * а не к ближайшему: человек целится в начало свободного куска, и подставленное
   * «14:45» вместо «14:30» он заметит уже после разговора с клиентом.
   */
  const minuteAt = (clientY: number, element: HTMLElement): number => {
    const box = element.getBoundingClientRect()
    const minutesFromOpen = (clientY - box.top) / perMinute
    return open + Math.floor(minutesFromOpen / step) * step
  }

  return (
    <div
      className={cx(
        styles["column"],
        pickable ? styles["columnPickable"] : undefined,
        props.actionsDisabled ? styles["columnLocked"] : undefined,
      )}
      style={{ left: `${props.left}px`, width: `${props.width}px`, height: `${height}px` }}
      /*
       * Признаки в разметке, а не только в классе: по ним колонку находит проверка,
       * и по ним же видно в отладчике, чья это колонка и почему в неё нельзя записать.
       */
      data-employee={column.employee.id}
      data-pickable={pickable ? "true" : "false"}
      onClick={(event) => {
        // Любое нажатие мимо удержания закрывает его объяснение: пузырь, который нечем
        // убрать, закрывает собой соседние записи.
        props.onExplain(null)
        if (!pickable) return
        props.onPickSlot?.(column.employee.id, minuteAt(event.clientY, event.currentTarget))
      }}
    >
      {/* Нерабочее время. Без смены штрихуется вся колонка: «сегодня не работает» —
          это ответ администратору, а не пустое место, в которое можно записать. */}
      {hasShift ? (
        <>
          {workTop > 0 ? (
            <div className={styles["offHours"]} style={{ top: 0, height: `${workTop}px` }} />
          ) : null}
          {workBottom < height ? (
            <div
              className={styles["offHours"]}
              style={{ top: `${workBottom}px`, height: `${height - workBottom}px` }}
            />
          ) : null}
          <div
            className={styles["columnStripe"]}
            style={{
              top: `${workTop}px`,
              height: `${Math.max(workBottom - workTop, 0)}px`,
              // Цвет мастера, введённый салоном, — это данные, а не решение оформления:
              // подставить его переменной темы нельзя, он у каждого свой.
              background: column.employee.color ?? "var(--border-strong)",
            }}
          />
        </>
      ) : (
        <div className={styles["offHours"]} style={{ top: 0, height: `${height}px` }} />
      )}

      {column.busy.map((block) => (
        <BusyBlock key={block.id} block={block} open={open} step={step} perMinute={perMinute} />
      ))}

      {column.pauses.map((pause) => (
        <div
          key={pause.id}
          className={styles["pause"]}
          style={{
            top: `${minuteToTop(pause.startMinute, open, step) + 1}px`,
            height: `${Math.round((pause.endMinute - pause.startMinute) * perMinute) - 2}px`,
          }}
          title={pause.label}
          /*
           * Пауза составной услуги — единственное занятое место, в которое ЗАПИСЫВАЮТ
           * (мастер свободен), поэтому нажатие сюда не гасится: оно открывает форму
           * на этом времени, как и на пустом месте колонки.
           */
        >
          {pause.label}
        </div>
      ))}

      {column.cards.map((card) => (
        <AppointmentCard
          key={card.id}
          card={card}
          top={minuteToTop(card.startMinute, open, step)}
          height={cardHeight(card.durationMinutes, step, card.minutesToNext)}
          onOpen={props.onOpenCard}
          selected={props.selectedCardId === card.id}
        />
      ))}

      {column.holds.map((hold) => (
        <HoldBlock
          key={hold.id}
          hold={hold}
          open={open}
          step={step}
          perMinute={perMinute}
          secondsNow={props.secondsNow}
          timeZone={props.timeZone}
          holdTtlSeconds={props.holdTtlSeconds}
          explained={props.explainedHoldId === hold.id}
          onExplain={props.onExplain}
          onReleaseHold={props.onReleaseHold}
          onRefresh={props.onRefresh}
        />
      ))}
    </div>
  )
}

function BusyBlock(props: {
  readonly block: JournalBusy
  readonly open: number
  readonly step: number
  readonly perMinute: number
}): ReactNode {
  const { block } = props
  const duration = block.endMinute - block.startMinute
  const height = Math.max(Math.round(duration * props.perMinute) - 2, 8)
  const kindClass =
    block.kind === "blocked"
      ? "busyBlocked"
      : block.isSystem || block.kind === "buffer" || block.kind === "cleanup"
        ? "busySystem"
        : "busyOwn"

  const label =
    block.kind === "break" ? `${block.label} · ${String(duration)} мин` : block.label

  return (
    <div
      className={cx(styles["busy"], styles[kindClass])}
      style={{
        top: `${minuteToTop(block.startMinute, props.open, props.step) + 1}px`,
        height: `${height}px`,
      }}
      title={label}
      /*
       * В обед и в буфер записать нельзя, поэтому нажатие сюда НЕ открывает форму:
       * форма, которая тут же отвечает «мастер занят», — это способ отнять у человека
       * минуту и ничего не сообщить заранее.
       */
      onClick={(event) => {
        event.stopPropagation()
      }}
    >
      {label}
    </div>
  )
}

function HoldBlock(props: {
  readonly hold: JournalHold
  readonly open: number
  readonly step: number
  readonly perMinute: number
  readonly secondsNow: number
  readonly timeZone: string
  readonly holdTtlSeconds: number | null
  readonly explained: boolean
  readonly onExplain: (holdId: string | null) => void
  readonly onReleaseHold?: ((holdId: string) => void) | undefined
  readonly onRefresh?: (() => void) | undefined
}): ReactNode {
  const { hold } = props
  const top = minuteToTop(hold.startMinute, props.open, props.step) + 1
  const height = Math.max(
    Math.round((hold.endMinute - hold.startMinute) * props.perMinute) - 2,
    18,
  )

  const remaining = (Date.parse(hold.expiresAt) - props.secondsNow) / 1000
  const explanation = holdExplanation({
    hold,
    timeZone: props.timeZone,
    nowMs: props.secondsNow,
    holdTtlSeconds: props.holdTtlSeconds,
  })

  // Освобождение времени администратор видит в ту же секунду, когда оно происходит:
  // он в этот момент разговаривает по телефону и решает — ждать или предложить другое.
  //
  // СВОЁ удержание подписано действием, а не отсчётом («держите вы · снять», 2-2.4.1):
  // отсчёт над чужим слотом — это тревога, а над своим — приглашение его освободить.
  const subtitle = hold.isQuarantine
    ? `В онлайн-запись вернётся в ${formatTimeOfDay(hold.expiresAt, props.timeZone)} · можно занять вручную`
    : remaining <= 0
      ? "Удержание истекло"
      : hold.isOwn
        ? "держите вы · снять"
        : `Освободится через ${formatCountdown(remaining)}`

  return (
    <>
      <div
        className={cx(styles["hold"], hold.isQuarantine ? styles["holdQuarantine"] : styles["holdOnline"])}
        style={{ top: `${top}px`, height: `${height}px` }}
        title={`${hold.title} · ${subtitle}`}
        /*
         * Удержание — не карточка и не блокировка: открыть его нечем, занять нельзя,
         * и молчаливое нажатие читается как поломка. Поэтому нажатие ОТВЕЧАЕТ словами
         * (2-2.4.1): кто держит, до какого времени и что можно сделать.
         *
         * Карантинное время занять вручную МОЖНО, поэтому у него нажатие не гасится
         * и доходит до колонки, открывая форму записи, — в этом весь смысл карантина.
         */
        {...(hold.isQuarantine
          ? {}
          : {
              role: "button",
              tabIndex: 0,
              "aria-expanded": props.explained,
              onClick: (event: MouseEvent) => {
                event.stopPropagation()
                props.onExplain(props.explained ? null : hold.id)
              },
              onKeyDown: (event: KeyboardEvent) => {
                if (event.key !== "Enter" && event.key !== " ") return
                event.preventDefault()
                event.stopPropagation()
                props.onExplain(props.explained ? null : hold.id)
              },
            })}
      >
        <div className={styles["holdTitle"]}>{hold.title}</div>
        {height >= 30 ? <div className={styles["holdSub"]}>{subtitle}</div> : null}
      </div>

      {props.explained ? (
        <div
          className={styles["holdBubble"]}
          role="status"
          style={{ top: `${top + height + 4}px` }}
          onClick={(event) => {
            event.stopPropagation()
          }}
        >
          <div className={styles["holdBubbleTitle"]}>{explanation.title}</div>
          <div>{explanation.text}</div>
          {explanation.action === "release" && props.onReleaseHold !== undefined ? (
            <button
              type="button"
              className={styles["holdBubbleAction"]}
              onClick={() => {
                props.onExplain(null)
                props.onReleaseHold?.(hold.id)
              }}
            >
              Снять удержание
            </button>
          ) : null}
          {explanation.action === "refresh" && props.onRefresh !== undefined ? (
            <button
              type="button"
              className={styles["holdBubbleAction"]}
              onClick={() => {
                props.onExplain(null)
                props.onRefresh?.()
              }}
            >
              Обновить
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  )
}

